import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSiteSettings } from '../../services/api';
import { Search, X, Languages, Pencil } from 'lucide-react';

// Fallback nav (used only if admin settings are unavailable)
const FALLBACK_NAV = [
  { label: 'ಮುಖಪುಟ', label_en: 'Home', path: '/', enabled: true },
  { label: 'ಕರ್ನಾಟಕ', label_en: 'Karnataka', path: '/category/cat_local', enabled: true },
  { label: 'ರಾಜಕೀಯ', label_en: 'Politics', path: '/category/cat_politics', enabled: true },
  { label: 'ವ್ಯಾಪಾರ', label_en: 'Business', path: '/category/cat_business', enabled: true },
  { label: 'ಕ್ರೀಡೆ', label_en: 'Sports', path: '/category/cat_sports', enabled: true },
  { label: 'ತಂತ್ರಜ್ಞಾನ', label_en: 'Technology', path: '/category/cat_technology', enabled: true },
  { label: 'ಮನರಂಜನೆ', label_en: 'Entertainment', path: '/category/cat_entertainment', enabled: true },
  { label: 'ಸಂಸ್ಕೃತಿ', label_en: 'Culture', path: '/category/cat_culture', enabled: true },
  { label: 'ಪಾಡ್‌ಕಾಸ್ಟ್', label_en: 'Podcasts', path: '/podcasts', enabled: true },
];

export default function Navbar() {
  const { user, login, logout } = useAuth();
  const { isKannada, toggleLang, t } = useLanguage();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [navItems, setNavItems] = useState(FALLBACK_NAV);
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = user && ['admin', 'editor', 'author'].includes(user.role);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 90);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Load menu items from admin settings so they stay in sync
  useEffect(() => {
    getSiteSettings()
      .then(res => {
        const items = res?.data?.header?.menu_items;
        if (Array.isArray(items) && items.length) {
          const mapped = items
            .filter(it => it && it.enabled !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(it => ({
              label: it.label_kn || it.label,
              label_en: it.label || it.label_kn,
              path: it.path || '/',
              enabled: true,
            }));
          if (mapped.length) setNavItems(mapped);
        }
      })
      .catch(() => {}); // fallback list already set
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchQ('');
      setSearchOpen(false);
    }
  };

  return (
    <header data-testid="navbar">
      {/* ── LOGO BAR ── */}
      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 flex items-center justify-between py-3">
          <Link to="/" data-testid="logo-link">
            <img src="/logo.svg" alt="Taaynadu News" className="h-12 sm:h-14" />
          </Link>
          <div className="flex items-center gap-3">
            {searchOpen ? (
              <form onSubmit={doSearch} className="flex items-center gap-2">
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} autoFocus placeholder={isKannada ? 'ಹುಡುಕಿ...' : 'Search...'} className="w-48 h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-500" data-testid="search-input" />
                <button type="button" onClick={() => setSearchOpen(false)} className="text-gray-400"><X className="w-4 h-4" /></button>
              </form>
            ) : (
              <button onClick={() => setSearchOpen(true)} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm" data-testid="search-toggle">
                <Search className="w-4 h-4" /> <span className="hidden sm:inline">{isKannada ? 'ಹುಡುಕಿ' : 'Search'}</span>
              </button>
            )}
            <button onClick={toggleLang} className="text-gray-400 hover:text-gray-700" data-testid="language-toggle" title={isKannada ? 'English' : 'ಕನ್ನಡ'}>
              <Languages className="w-4 h-4" />
            </button>
            {user ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{user.name?.split(' ')[0]}</span>
                {user.role === 'admin' && <Link to="/admin" className="text-red-600 font-bold hover:underline" data-testid="admin-panel-link">Admin</Link>}
                {isStaff && <Link to="/editor" className="text-red-600 font-bold hover:underline" data-testid="editor-panel-link">Editor</Link>}
                <button onClick={logout} className="text-gray-400 hover:text-red-600" data-testid="logout-btn">Logout</button>
              </div>
            ) : (
              <button onClick={login} className="text-sm text-gray-500 hover:text-gray-900" data-testid="login-btn">{t('login')}</button>
            )}
          </div>
        </div>
      </div>

      {/* ── NAV BAR ── */}
      <nav className={`bg-white border-t border-b border-gray-200 ${scrolled ? 'fixed top-0 left-0 right-0 z-50 shadow-sm' : ''}`} data-testid="main-nav">
        <div className="max-w-[1400px] mx-auto px-4 flex items-center overflow-x-auto scrollbar-thin gap-0">
          {navItems.map((item, i) => {
            const active =
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const display = isKannada ? (item.label || item.label_en) : (item.label_en || item.label);
            return (
              <Link
                key={`${item.path}-${i}`}
                to={item.path}
                className={`px-4 py-3 whitespace-nowrap transition-colors border-b-2 ${active ? 'text-red-600 border-red-600' : 'text-gray-700 border-transparent hover:text-red-600'}`}
                data-testid={`nav-${item.label_en || item.path}`}
              >
                {display}
              </Link>
            );
          })}
        </div>
      </nav>
    </header>
  );
}
